import React, { useLayoutEffect, useRef, useState } from "react";

import { FileSymlink, Loader2, Maximize2, Minimize2, Save, X } from "lucide-react";

import { Button } from "@/components/ui/button";

const POPOVER_WIDTH = 320; // px, matches w-[320px]
const VIEWPORT_MARGIN = 8; // px, keep clear of the viewport edges
const ANCHOR_GAP = 8; // px, gap between the selection and the popover

/** Viewport-space rectangle of the text selection to anchor beside. */
export type SnipAnchorRect = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

type SnipResultPopoverProps = {
  title?: string;
  text?: string;
  source?: string;
  lineCount?: number;
  pageNumber?: number;
  isLoading: boolean;
  isProcessing: boolean;
  error: string | null;
  onClose: () => void;
  onSaveNote?: () => void;
  onMapToReportBuilder?: () => void;
  /**
   * When set, the popover is anchored (position: fixed) beside this
   * viewport-space rect — placed below the selection, clamped to the viewport.
   * When omitted, it falls back to the pinned bottom-right placement.
   */
  anchorRect?: SnipAnchorRect | null;
};

export const SnipResultPopover: React.FC<SnipResultPopoverProps> = ({
  title = "OCR Result",
  text,
  pageNumber,
  isLoading,
  isProcessing,
  error,
  onClose,
  onSaveNote,
  onMapToReportBuilder,
  anchorRect,
}) => {
  const [expanded, setExpanded] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // When anchored, place the popover just below the selection, left-aligned to
  // it, then clamp into the viewport (flip above if it would overflow the
  // bottom). Recomputed after layout so we know the rendered height.
  const [anchoredStyle, setAnchoredStyle] = useState<React.CSSProperties | null>(
    null
  );
  useLayoutEffect(() => {
    if (!anchorRect) {
      setAnchoredStyle(null);
      return;
    }
    const el = popoverRef.current;
    const height = el?.offsetHeight ?? 0;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = anchorRect.left;
    left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(left, vw - POPOVER_WIDTH - VIEWPORT_MARGIN)
    );

    // Prefer below the selection; flip above if it would run off the bottom.
    let top = anchorRect.bottom + ANCHOR_GAP;
    if (top + height > vh - VIEWPORT_MARGIN) {
      const above = anchorRect.top - ANCHOR_GAP - height;
      top = above >= VIEWPORT_MARGIN ? above : Math.max(VIEWPORT_MARGIN, top);
    }

    setAnchoredStyle({ position: "fixed", top, left });
  }, [anchorRect, expanded, isLoading, isProcessing, error, text]);

  const hasResult = !isLoading && !isProcessing && !error && !!text;

  return (
    <div
      ref={popoverRef}
      style={anchoredStyle ?? undefined}
      className={
        anchoredStyle
          ? "z-30 w-[320px] rounded-lg border bg-background-white shadow-lg"
          : "absolute bottom-4 right-4 z-30 w-[320px] rounded-lg border bg-background-white shadow-lg"
      }
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="text-lg font-bold">{title}</h3>
        <div className="flex items-center gap-1">
          {hasResult && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setExpanded(!expanded)}
              title={expanded ? "Collapse" : "Expand"}
            >
              {expanded ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="px-4 py-3">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-foreground-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            Extracting text from selection...
          </div>
        )}

        {/* Processing state */}
        {isProcessing && (
          <div className="flex items-center gap-2 text-sm text-foreground-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            OCR layout data is being generated. Please try again in a few moments.
          </div>
        )}

        {/* Error state */}
        {error && <p className="text-sm text-destructive">{error}</p>}

        {/* Empty state */}
        {!isLoading && !isProcessing && !error && !text && (
          <p className="text-sm text-foreground-muted">
            No text found in the selected region.
          </p>
        )}

        {/* Result */}
        {hasResult && (
          <>
            {pageNumber && (
              <p className="text-muted-foreground mb-2 text-sm">Page {pageNumber}</p>
            )}

            <div
              className={`overflow-auto rounded-md border bg-background-muted p-3 ${
                expanded ? "max-h-[400px]" : "max-h-[120px]"
              }`}
            >
              <p className="whitespace-pre-wrap text-sm">{text}</p>
            </div>

            {/* Action buttons */}
            <div className="mt-3 flex items-center justify-end gap-2">
              {/* TODO: Wire up Save Note functionality
              <Button
                variant="default"
                size="sm"
                onClick={() => onSaveNote?.()}
              >
                <Save className="mr-1.5 h-3.5 w-3.5" />
                Save Note
              </Button>
              */}
              <Button size="sm" onClick={() => onMapToReportBuilder?.()}>
                <FileSymlink className="size-3.5" />
                Map to Report Builder
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
